import React, { useState } from 'react';
import { Typography, Box, Avatar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

const TestimonialItem = ({ imgSrc, opinionText, userName }) => (
    <Box
        sx={{
            display: 'flex',
            gap: { xs: 3, md: 6 },
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            textAlign: { xs: 'center', md: 'left' },
            maxWidth: '100%',
        }}
    >
        <Typography
            sx={{
                lineHeight: 1.75,
                fontSize: { xs: '14px', md: '18px' },
                color: '#848dac',
                fontFamily: 'Barlow, sans-serif',
                flex: 1,
                fontStyle: 'italic',
            }}
        >
            "{opinionText}"
        </Typography>

        <Box
            sx={{
                flexShrink: 0,
                display: 'flex',
                textAlign: 'center',
                alignItems: 'center',
                flexDirection: 'column',
            }}
        >
            <Avatar 
                alt={userName} 
                src={imgSrc} 
                sx={{ 
                    width: { xs: 60, md: 80 }, 
                    height: { xs: 60, md: 80 },
                    border: '3px solid #ecf4fc',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }} 
            />

            <Typography 
                variant="h6" 
                sx={{ 
                    mt: 2, 
                    mb: 0.5,
                    fontFamily: 'Barlow, sans-serif',
                    fontSize: { xs: '14px', md: '16px' },
                    fontWeight: 600,
                    color: '#00398F',
                }}
            >
                {userName}
            </Typography>
        </Box>
    </Box>
);

export default function OpinionsFromUsers() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeOpinion, setActiveOpinion] = useState(0);

    const opinions = [
        {
            imgSrc: '/images/cristina.png',
            opinionText: t('OpinionsFromUsers02'),
            userName: 'Cristina, CB Sant Antoni',
        },
        {
            imgSrc: '/images/william.png',
            opinionText: t('OpinionsFromUsers03'),
            userName: 'William, BK Amager',
        },
        {
            imgSrc: '/images/peptoni.png',
            opinionText: t('OpinionsFromUsers04'),
            userName: 'Pep Toni, CB Muro',
        },
    ];

    const handleNext = () => {
        setActiveOpinion((activeOpinion + 1) % opinions.length);
    };

    const handlePrevious = () => {
        setActiveOpinion((activeOpinion - 1 + opinions.length) % opinions.length);
    };

    const renderTexts = () => (
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
            <Typography 
                variant="overline" 
                sx={{ 
                    mb: 2, 
                    display: 'block', 
                    color: 'text.disabled',
                    fontFamily: 'Barlow, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                }}
            >
                {t('OpinionsFromUsers05')}
            </Typography>

            <Typography 
                variant="h2"
                sx={{
                    fontFamily: 'Barlow, sans-serif',
                    fontSize: { xs: '25px', md: '40px' },
                    fontWeight: 'bold',
                    color: '#1a202c',
                    lineHeight: 1.1,
                }}
            >
                {t('OpinionsFromUsers06')}
            </Typography>
        </Box>
    );

    const renderCarousel = () => (
        <>
            {!isMobile && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: { xs: 10, md: -60 },
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                    }}
                >
                    <IconButton
                        onClick={handlePrevious}
                        sx={{
                            borderRadius: '50%',
                            color: '#00398F',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                                transform: 'scale(1.1)',
                            },
                        }}
                    >
                        <ArrowBackIosNewRoundedIcon />
                    </IconButton>
                </Box>
            )}

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    p: { xs: 3, md: 4 },
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
            >
                <TestimonialItem
                    imgSrc={opinions[activeOpinion].imgSrc}
                    opinionText={opinions[activeOpinion].opinionText}
                    userName={opinions[activeOpinion].userName}
                />
            </Box>

            {!isMobile && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: { xs: 10, md: -60 },
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                    }}
                >
                    <IconButton
                        onClick={handleNext}
                        sx={{
                            borderRadius: '50%',
                            color: '#00398F',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 1)',
                                transform: 'scale(1.1)',
                            },
                        }}
                    >
                        <ArrowForwardIosRoundedIcon />
                    </IconButton>
                </Box>
            )}

            {isMobile && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 3,
                    }}
                >
                    <IconButton
                        onClick={handlePrevious}
                        sx={{
                            borderRadius: '50%',
                            color: '#00398F',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <ArrowBackIosNewRoundedIcon />
                    </IconButton>
                    <IconButton
                        onClick={handleNext}
                        sx={{
                            borderRadius: '50%',
                            color: '#00398F',
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        <ArrowForwardIosRoundedIcon />
                    </IconButton>
                </Box>
            )}

            {/* Dots indicator */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 3,
                }}
            >
                {opinions.map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: index === activeOpinion ? '#00398F' : 'rgba(0, 57, 143, 0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: '#00398F',
                                transform: 'scale(1.2)',
                            },
                        }}
                        onClick={() => setActiveOpinion(index)}
                    />
                ))}
            </Box>
        </>
    );

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 10, md: 15 },
                bgcolor: '#ecf4fc',
                position: 'relative',
            }}
        >
            <Box 
                sx={{ 
                    maxWidth: 720, 
                    margin: 'auto',
                    position: 'relative',
                    px: { xs: 2, md: 4 },
                }}
            >
                {renderTexts()}
                {renderCarousel()}
            </Box>
        </Box>
    );
}